import { observer } from 'mobx-react-lite';
import { useEffect, useImperativeHandle, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import modalStore from '@client/store/modalStore';
import {
    MarkdownTextarea,
    MarkdownTextareaChangeEvent,
    MarkdownTextareaRef,
} from './MarkdownTextarea';

type Status = 'idle' | 'dirty' | 'saving' | 'saved' | 'error';

export interface MarkdownEditorRef {
    setInitialContent: ({ title, content }: { title?: string; content: string }) => void;
    getContent: () => { title: string; content: string };
}

export const MarkdownEditor = observer(
    ({
        ref,

        useTitle = false,

        titlePlaceholder = '제목',
        titleClassName,
        titleMinRows = 1,
        titleMaxRows,
        titleTrim = true,

        contentPlaceholder = '내용',
        contentClassName,
        contentMinRows = 10,
        contentMaxRows,
        contentTrim = true,

        saveButtonLabel = '저장',
        cancelButtonLabel = '취소',

        onChange,
        onClickSave,
        onClickCancel,
    }: {
        /**
         * 에디터는 title, content 를 props 로 넘기면 관리가 애매하므로 명시적으로 ref 로 처리한다
         */
        ref?: React.Ref<MarkdownEditorRef>;

        useTitle: boolean;

        titlePlaceholder?: string;
        titleClassName?: string;
        titleMinRows?: number;
        titleMaxRows?: number;
        titleTrim?: boolean;

        contentPlaceholder?: string;
        contentClassName?: string;
        contentMinRows?: number;
        contentMaxRows?: number;
        contentTrim?: boolean;

        saveButtonLabel?: string;
        cancelButtonLabel?: string;

        onChange?: ({
            title,
            content,
            event,
        }: {
            title: string;
            content: string;
            event: MarkdownTextareaChangeEvent;
        }) => void;
        onClickSave?: ({ title, content }: { title: string; content: string }) => Promise<void>;
        onClickCancel?: () => void;
    }) => {
        const titleRef = useRef<MarkdownTextareaRef>(null);
        const contentRef = useRef<MarkdownTextareaRef>(null);

        const [status, setStatus] = useState<Status>('idle');

        useEffect(() => {
            const onBeforeUnload = (e: BeforeUnloadEvent) => {
                if (status === 'dirty' || status === 'saving') {
                    e.preventDefault();
                    e.returnValue = '';
                }
            };

            window.addEventListener('beforeunload', onBeforeUnload);

            return () => {
                window.removeEventListener('beforeunload', onBeforeUnload);
            };
        }, [status]);

        useImperativeHandle(ref, () => {
            return {
                setInitialContent: ({ title, content }: { title?: string; content: string }) => {
                    titleRef.current?.setInitialContent(title || '');
                    contentRef.current?.setInitialContent(content);
                },
                getContent: () => {
                    return {
                        title: titleRef.current?.getContent() || '',
                        content: contentRef.current?.getContent() || '',
                    };
                },
            };
        }, []);

        const isSaving = useRef(false);

        const handleSave = async () => {
            if (isSaving.current) {
                return;
            }

            isSaving.current = true;
            setStatus('saving');

            try {
                let title = titleRef.current?.getContent() || '';
                let content = contentRef.current?.getContent() || '';

                if (titleTrim) {
                    title = title.trim();
                }
                if (contentTrim) {
                    content = content.trim();
                }

                await onClickSave?.({
                    title,
                    content,
                });

                setStatus('saved');

                // 저장한 정보로 textarea 초기화 (isChanged 초기화를 위함)
                titleRef.current?.setInitialContent(title);
                contentRef.current?.setInitialContent(content);
            } catch (error) {
                console.error(error);
                setStatus('error');
            } finally {
                isSaving.current = false;
            }
        };

        const handleCancel = async () => {
            if (status === 'dirty') {
                const { returnValue } = await modalStore.confirm({
                    title: '정말 취소하시겠습니까?',
                    message: '취소하면 작성한 내용이 사라집니다.',
                });

                if (returnValue) {
                    onClickCancel?.();
                    setStatus('idle');
                }
            } else {
                onClickCancel?.();
                setStatus('idle');
            }
        };

        const handleChange = (e: MarkdownTextareaChangeEvent) => {
            if (titleRef.current?.isDirty() || contentRef.current?.isDirty()) {
                setStatus('dirty');
            } else {
                setStatus('idle');
            }

            onChange?.({
                title: titleRef.current?.getContent() || '',
                content: contentRef.current?.getContent() || '',
                event: e,
            });
        };

        return (
            <div className="flex flex-col gap-2.5">
                {useTitle && (
                    <MarkdownTextarea
                        ref={titleRef}
                        minRows={titleMinRows}
                        maxRows={titleMaxRows}
                        placeholder={titlePlaceholder}
                        className={twMerge(titleClassName)}
                        disabled={status === 'saving'}
                        onChange={(e: MarkdownTextareaChangeEvent) => {
                            handleChange(e);
                        }}
                    />
                )}
                <MarkdownTextarea
                    ref={contentRef}
                    minRows={contentMinRows}
                    maxRows={contentMaxRows}
                    placeholder={contentPlaceholder}
                    className={twMerge(contentClassName)}
                    disabled={status === 'saving'}
                    onChange={(e: MarkdownTextareaChangeEvent) => {
                        handleChange(e);
                    }}
                />
                <div className={twMerge('flex justify-end gap-2')}>
                    {onClickCancel && (
                        <button
                            className={twMerge('btn btn-ghost btn-sm')}
                            onClick={(e) => {
                                e.preventDefault();
                                handleCancel();
                            }}
                        >
                            {cancelButtonLabel}
                        </button>
                    )}
                    <button
                        className="btn btn-sm btn-soft btn-primary px-6"
                        onClick={(e) => {
                            e.preventDefault();
                            handleSave();
                        }}
                        disabled={status === 'idle' || status === 'saving' || status === 'saved'}
                    >
                        {status === 'saving' ? (
                            <span className="loading loading-dots loading-xs bg-base-content/10"></span>
                        ) : (
                            saveButtonLabel
                        )}
                    </button>
                </div>
            </div>
        );
    },
);
